/**
 * OlvCostCell — shared ⓘ-popup Work-Unit cell (R15 unit-lockstep, 2026-08-09;
 * PRO Work Units migration, 2026-08-13).
 *
 * Renders a per-call amount in PRO Work Units, swapping to USD while the
 * POPUP-level showUsd flag is set. The R15 rule is unchanged: hovering any
 * cell flips the WHOLE popup together (the old per-cell hover state produced
 * mixed units inside one breakdown); both values render stacked in one grid
 * cell (container sizes to the larger) so there is no layout shift, and the
 * hidden value is aria-hidden so screen readers hear exactly one amount.
 *
 * 2026-08-13 (WU migration): pr0 → WU. The displayed WU is derived from the
 * SAME user-cost USD that previously powered the pr0 display, at the fixed
 * Standard Rate ($2.18/WU — src/config/workUnits.ts). No live OLV rate is
 * involved anymore, so the Firestore rate listener dependency is gone.
 *
 * Shared by GlobalChatSystem.tsx and SpecialMessageTile.tsx — these two
 * popups have drifted before (see the ORG-USAGE-2026 lockstep note); one
 * component makes unit-behavior drift impossible.
 */
import React from 'react';
import { formatCost } from '../../config/aiModelPricing';
import { usdToWu, formatWuDetail, WU_LABEL } from '../../config/workUnits';

export const OlvCostCell: React.FC<{
  usdCost: number | undefined;
  showUsd: boolean;
  onHoverChange: (hovering: boolean) => void;
}> = ({ usdCost, showUsd, onHoverChange }) => {
  const isZero = usdCost === undefined || usdCost === null || usdCost === 0;
  // 2026-08-14 (Miles): per-call cells are DETAIL surfaces — formatWuDetail
  // shows the real sub-0.01 quantity (≥2 significant figures) instead of the
  // old "<0.01" floor that made every cheap call look identical.
  const wuFormatted = isZero
    ? `0 ${WU_LABEL}`
    : `${formatWuDetail(usdToWu(usdCost))} ${WU_LABEL}`;
  const usdFormatted = isZero ? '$0.00' : formatCost(usdCost);
  return (
    <span
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
      style={{ cursor: 'pointer', display: 'inline-grid' }}
    >
      <span
        style={{ gridArea: '1 / 1', opacity: showUsd ? 0 : 1, transition: 'opacity 0.15s ease-in-out', whiteSpace: 'nowrap' }}
        aria-hidden={showUsd}
      >
        {wuFormatted}
      </span>
      <span
        style={{ gridArea: '1 / 1', opacity: showUsd ? 1 : 0, transition: 'opacity 0.15s ease-in-out', whiteSpace: 'nowrap' }}
        aria-hidden={!showUsd}
      >
        {usdFormatted}
      </span>
    </span>
  );
};
